import { useFormContext, useWatch } from 'react-hook-form';
import FormRadioBtn from 'components/common/forms/FormRadioBtn';
import FormAutoHeightTextarea from 'components/common/forms/FormAutoHeightTextarea';
import AllergyCheckbox from 'components/pages/chart/medicalHistoryChart/AllergyCheckbox';
import DiseaseExperience from 'components/pages/chart/medicalHistoryChart/DiseaseExperience';
import styles from 'styles/pages/chart/MedicalHistoryChart.module.scss';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import {
  HealthStatusList,
  MedicalHistoryChartFields,
  MedicalHistoryChartRadioBtn,
  MedicalHistoryChartRadioBtnLabel,
  PregnancyRadioBtn,
  PregnancyRadioBtnLabel,
} from 'constants/chart/medicalHistoryChart';
import useModal from 'hooks/useModal';

const numberStyle = {
  backgroundColor: STYLES_VARIABLES.GRAY_01,
  color: STYLES_VARIABLES.GRAY_07,
  fontSize: '24px',
  fontWeight: '700',
  width: '60px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderTopLeftRadius: '8px',
  borderBottomLeftRadius: '8px',
};

type TProps = {
  isRegister: boolean;
  isReadOnly: boolean;
};

const MedicalHistoryChart = ({ isRegister, isReadOnly }: TProps) => {
  const { openModal } = useModal();

  const { register, setValue, control } = useFormContext();

  const surgeryCheckWatch = useWatch({
    name: MedicalHistoryChartFields.SurgeryCheck,
    control,
  });
  const surgeryDetailWatch = useWatch({
    name: MedicalHistoryChartFields.SurgeryDetail,
    control,
  });

  const diseaseCheckWatch = useWatch({
    name: MedicalHistoryChartFields.DiseaseCheck,
    control,
  });
  const diseaseExperienceWatch = useWatch({
    name: MedicalHistoryChartFields.DiseaseExperience,
    control,
  });
  const diseaseDetailWatch = useWatch({
    name: [
      MedicalHistoryChartFields.DiseaseName,
      MedicalHistoryChartFields.DiseaseDrugName,
    ],
    control,
  });

  const smokingCheckWatch = useWatch({
    name: MedicalHistoryChartFields.SmokingCheck,
    control,
  });
  const smokingDetailWatch = useWatch({
    name: [
      MedicalHistoryChartFields.SmokingPeriod,
      MedicalHistoryChartFields.AmountOfSmoking,
    ],
    control,
  });

  const alcholCheckWatch = useWatch({
    name: MedicalHistoryChartFields.AlcholCheck,
    control,
  });
  const alcholDetailWatch = useWatch({
    name: [
      MedicalHistoryChartFields.AlcholFrequency,
      MedicalHistoryChartFields.AmountOfAlchol,
    ],
    control,
  });

  const pregnancyCheckWatch = useWatch({
    name: MedicalHistoryChartFields.PregnancyCheck,
    control,
  });
  const pregnancyDetailWatch = useWatch({
    name: MedicalHistoryChartFields.PregnancyPeriod,
    control,
  });

  return (
    <>
      <div className={styles.questions_wrapper}>
        <div className={styles.question_wrapper}>
          <div style={numberStyle}>1</div>
          <div className={styles.question_content_wrapper}>
            <p className="fs16 fw700">본인의 건강상태를 어떻게 생각하십니까?</p>
            <div className="flex mt12">
              {HealthStatusList.map((item) => (
                <FormRadioBtn
                  key={item.value}
                  name={MedicalHistoryChartFields.HealthCheck}
                  labelText={item.label}
                  value={item.value}
                  labelStyle={{ marginRight: '80px' }}
                  readOnly={!isRegister || isReadOnly}
                />
              ))}
            </div>
          </div>
        </div>

        <div className={styles.question_wrapper}>
          <div style={numberStyle}>2</div>
          <div className={styles.question_content_wrapper}>
            <p className="fs16 fw700">
              최근 5년 이내에 수술을 받거나 병원에 입원한 경험이 있습니까?
            </p>
            <div className={styles.question_col_options}>
              <div className="flex justify_between w100">
                <FormRadioBtn
                  name={MedicalHistoryChartFields.SurgeryCheck}
                  labelText={
                    MedicalHistoryChartRadioBtnLabel[
                      MedicalHistoryChartRadioBtn.Yes
                    ]
                  }
                  value={MedicalHistoryChartRadioBtn.Yes}
                  readOnly={!isRegister || isReadOnly}
                />
                <FormAutoHeightTextarea
                  name={MedicalHistoryChartFields.SurgeryDetail}
                  placeholder='"예"를 선택한 경우, 해당 경험에 대해 작성해 주세요.'
                  textareaStyle={{ width: '100%' }}
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    surgeryCheckWatch !== MedicalHistoryChartRadioBtn.Yes
                  }
                />
              </div>
              <FormRadioBtn
                name={MedicalHistoryChartFields.SurgeryCheck}
                labelText={
                  MedicalHistoryChartRadioBtnLabel[
                    MedicalHistoryChartRadioBtn.No
                  ]
                }
                value={MedicalHistoryChartRadioBtn.No}
                afterChange={() => {
                  if (surgeryDetailWatch) {
                    openModal({
                      isConfirm: true,
                      title: '작성 중인 내용을 삭제하시겠습니까?',
                      text: '작성 중인 내용이 삭제됩니다.',
                      onCancel: () => {
                        setValue(
                          MedicalHistoryChartFields.SurgeryCheck,
                          MedicalHistoryChartRadioBtn.Yes
                        );
                      },
                      onSubmit: () => {
                        setValue(MedicalHistoryChartFields.SurgeryDetail, '');
                      },
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.question_wrapper}>
          <div style={numberStyle}>3</div>
          <div className={styles.question_content_wrapper}>
            <p className="fs16 fw700">
              아래 나열된 질환과 관련하여, 현재까지 경험한 질환이나 현재
              복용하고 있는 약물이 있습니까?
            </p>
            <div className={styles.question_col_options}>
              <div className={styles.question_col_wrapper}>
                <FormRadioBtn
                  name={MedicalHistoryChartFields.DiseaseCheck}
                  value={MedicalHistoryChartRadioBtn.Yes}
                  labelText={
                    MedicalHistoryChartRadioBtnLabel[
                      MedicalHistoryChartRadioBtn.Yes
                    ]
                  }
                  readOnly={!isRegister || isReadOnly}
                />
                <FormAutoHeightTextarea
                  name={MedicalHistoryChartFields.DiseaseName}
                  placeholder="현재 약물 복용 중인 질환명을 작성해 주세요."
                  textareaStyle={{ width: '100%' }}
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    diseaseCheckWatch !== MedicalHistoryChartRadioBtn.Yes
                  }
                />

                <span className="ml8">&#47;</span>

                <FormAutoHeightTextarea
                  name={MedicalHistoryChartFields.DiseaseDrugName}
                  placeholder="현재 약물 복용 중인 약물을 작성해 주세요."
                  textareaStyle={{ width: '100%' }}
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    diseaseCheckWatch !== MedicalHistoryChartRadioBtn.Yes
                  }
                />
              </div>

              <div
                style={{
                  backgroundColor: STYLES_VARIABLES.GRAY_01,
                }}
                className={styles.sub_question_wrapper}
              >
                <span className="fw700">
                  현재까지 경험한 질환을{' '}
                  <span className="text_underline">모두 체크</span>해 주세요.
                </span>
                <DiseaseExperience isRegister={isRegister && !isReadOnly} />
              </div>

              <FormRadioBtn
                name={MedicalHistoryChartFields.DiseaseCheck}
                value={
                  MedicalHistoryChartRadioBtnLabel[
                    MedicalHistoryChartRadioBtn.No
                  ]
                }
                labelText={MedicalHistoryChartRadioBtn.No}
                readOnly={!isRegister || isReadOnly}
                afterChange={() => {
                  if (
                    diseaseDetailWatch.filter((item) => !!item).length > 0 ||
                    diseaseExperienceWatch.length > 0
                  ) {
                    openModal({
                      isConfirm: true,
                      title: '작성 중인 내용을 삭제하시겠습니까?',
                      text: '작성 중인 내용이 삭제됩니다.',
                      onCancel: () => {
                        setValue(
                          MedicalHistoryChartFields.DiseaseCheck,
                          MedicalHistoryChartRadioBtn.Yes
                        );
                      },
                      onSubmit: () => {
                        setValue(
                          MedicalHistoryChartFields.DiseaseExperience,
                          []
                        );
                        setValue(MedicalHistoryChartFields.DiseaseName, '');
                        setValue(MedicalHistoryChartFields.DiseaseDrugName, '');
                      },
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.question_wrapper}>
          <div style={numberStyle}>4</div>
          <div className={styles.question_content_wrapper}>
            <p className="fs16 fw700">현재 흡연을 하고 계십니까?</p>
            <div className={styles.question_options_row}>
              <div className="flex justify_between">
                <FormRadioBtn
                  name={MedicalHistoryChartFields.SmokingCheck}
                  value={MedicalHistoryChartRadioBtn.Yes}
                  labelText={
                    MedicalHistoryChartRadioBtnLabel[
                      MedicalHistoryChartRadioBtn.Yes
                    ]
                  }
                  readOnly={!isRegister || isReadOnly}
                />
                <div className="flex items_center">
                  <div className="flex items_center ml8">
                    <input
                      {...register(MedicalHistoryChartFields.SmokingPeriod)}
                      type="number"
                      className={styles.input_sm}
                      style={{ width: '84px' }}
                      placeholder="흡연 기간"
                      readOnly={
                        !isRegister ||
                        isReadOnly ||
                        smokingCheckWatch !== MedicalHistoryChartRadioBtn.Yes
                      }
                    />
                    <span className="ml8">년</span>
                  </div>
                  <span className="fw500 mx8">&#47;</span>
                  <div className="flex items_center">
                    <input
                      {...register(MedicalHistoryChartFields.AmountOfSmoking)}
                      type="number"
                      className={styles.input_sm}
                      style={{ width: '64px' }}
                      placeholder="흡연양"
                      readOnly={
                        !isRegister ||
                        isReadOnly ||
                        smokingCheckWatch !== MedicalHistoryChartRadioBtn.Yes
                      }
                    />
                    <span className="ml8">개피/일</span>
                  </div>
                </div>
              </div>
              <FormRadioBtn
                name={MedicalHistoryChartFields.SmokingCheck}
                value={MedicalHistoryChartRadioBtn.No}
                labelText={
                  MedicalHistoryChartRadioBtnLabel[
                    MedicalHistoryChartRadioBtn.No
                  ]
                }
                readOnly={!isRegister || isReadOnly}
                afterChange={() => {
                  if (smokingDetailWatch.filter((item) => !!item).length > 0) {
                    openModal({
                      isConfirm: true,
                      title: '작성 중인 내용을 삭제하시겠습니까?',
                      text: '작성 중인 내용이 삭제됩니다.',
                      onCancel: () => {
                        setValue(
                          MedicalHistoryChartFields.SmokingCheck,
                          MedicalHistoryChartRadioBtn.Yes
                        );
                      },
                      onSubmit: () => {
                        setValue(MedicalHistoryChartFields.SmokingPeriod, '');
                        setValue(MedicalHistoryChartFields.AmountOfSmoking, '');
                      },
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.question_wrapper}>
          <div style={numberStyle}>5</div>
          <div className={styles.question_content_wrapper}>
            <p className="fs16 fw700">
              음주여부와 빈도 및 음주량은 얼마입니까?
            </p>
            <div className={styles.question_options_row}>
              <div className="flex justify_between">
                <FormRadioBtn
                  name={MedicalHistoryChartFields.AlcholCheck}
                  value={MedicalHistoryChartRadioBtn.Yes}
                  labelText={
                    MedicalHistoryChartRadioBtnLabel[
                      MedicalHistoryChartRadioBtn.Yes
                    ]
                  }
                  readOnly={!isRegister || isReadOnly}
                />
                <div className="flex items_center">
                  <div className="flex items_center ml8">
                    <input
                      {...register(MedicalHistoryChartFields.AlcholFrequency)}
                      type="number"
                      className={styles.input_sm}
                      style={{ width: '84px' }}
                      placeholder="예) 10"
                      readOnly={
                        !isRegister ||
                        isReadOnly ||
                        alcholCheckWatch !== MedicalHistoryChartRadioBtn.Yes
                      }
                    />
                    <span className="ml8">회</span>
                  </div>
                  <span className="fw500 mx8">&#47;</span>
                  <div className="flex items_center">
                    <input
                      {...register(MedicalHistoryChartFields.AmountOfAlchol)}
                      type="text"
                      className={styles.input_sm}
                      style={{ width: '64px' }}
                      placeholder="예) 10"
                      readOnly={
                        !isRegister ||
                        isReadOnly ||
                        alcholCheckWatch !== MedicalHistoryChartRadioBtn.Yes
                      }
                    />
                    <span className="ml8">병/종류</span>
                  </div>
                </div>
              </div>
              <FormRadioBtn
                name={MedicalHistoryChartFields.AlcholCheck}
                value={MedicalHistoryChartRadioBtn.No}
                labelText={
                  MedicalHistoryChartRadioBtnLabel[
                    MedicalHistoryChartRadioBtn.No
                  ]
                }
                readOnly={!isRegister || isReadOnly}
                afterChange={() => {
                  if (alcholDetailWatch.filter((item) => !!item).length > 0) {
                    openModal({
                      isConfirm: true,
                      title: '작성 중인 내용을 삭제하시겠습니까?',
                      text: '작성 중인 내용이 삭제됩니다.',
                      onCancel: () => {
                        setValue(
                          MedicalHistoryChartFields.AlcholCheck,
                          MedicalHistoryChartRadioBtn.Yes
                        );
                      },
                      onSubmit: () => {
                        setValue(MedicalHistoryChartFields.AlcholFrequency, '');
                        setValue(MedicalHistoryChartFields.AmountOfAlchol, '');
                      },
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.question_wrapper}>
          <div style={numberStyle}>6</div>
          <div className={styles.question_content_wrapper}>
            <p className="fs16 fw700">
              현재 임신 중 이십니까? (여성분만 체크해 주세요)
            </p>
            <div className="flex mt12">
              <div className="inline_flex justify_between mr32">
                <FormRadioBtn
                  name={MedicalHistoryChartFields.PregnancyCheck}
                  value={PregnancyRadioBtn.Yes}
                  labelText={PregnancyRadioBtnLabel[PregnancyRadioBtn.Yes]}
                  readOnly={!isRegister || isReadOnly}
                />
                <div className="flex items_center">
                  <div className="flex items_center ml8">
                    <input
                      {...register(MedicalHistoryChartFields.PregnancyPeriod)}
                      type="number"
                      className={styles.input_sm}
                      style={{ width: '88px' }}
                      placeholder="예) 10"
                      readOnly={
                        !isRegister ||
                        isReadOnly ||
                        pregnancyCheckWatch !== PregnancyRadioBtn.Yes
                      }
                    />
                    <span className="ml8">개월</span>
                  </div>
                </div>
              </div>
              <FormRadioBtn
                name={MedicalHistoryChartFields.PregnancyCheck}
                value={PregnancyRadioBtn.No}
                labelText={PregnancyRadioBtnLabel[PregnancyRadioBtn.No]}
                labelStyle={{ marginRight: '32px' }}
                readOnly={!isRegister || isReadOnly}
                afterChange={() => {
                  if (pregnancyDetailWatch) {
                    openModal({
                      isConfirm: true,
                      title: '작성 중인 내용을 삭제하시겠습니까?',
                      text: '작성 중인 내용이 삭제됩니다.',
                      onCancel: () => {
                        setValue(
                          MedicalHistoryChartFields.PregnancyCheck,
                          MedicalHistoryChartRadioBtn.Yes
                        );
                      },
                      onSubmit: () => {
                        setValue(MedicalHistoryChartFields.PregnancyPeriod, '');
                      },
                    });
                  }
                }}
              />
              <FormRadioBtn
                name={MedicalHistoryChartFields.PregnancyCheck}
                value={PregnancyRadioBtn.Etc}
                labelText={PregnancyRadioBtnLabel[PregnancyRadioBtn.Etc]}
                readOnly={!isRegister || isReadOnly}
                afterChange={() => {
                  if (pregnancyDetailWatch) {
                    openModal({
                      isConfirm: true,
                      title: '작성 중인 내용을 삭제하시겠습니까?',
                      text: '작성 중인 내용이 삭제됩니다.',
                      onCancel: () => {
                        setValue(
                          MedicalHistoryChartFields.PregnancyCheck,
                          MedicalHistoryChartRadioBtn.Yes
                        );
                      },
                      onSubmit: () => {
                        setValue(MedicalHistoryChartFields.PregnancyPeriod, '');
                      },
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.question_wrapper}>
          <div style={numberStyle}>7</div>
          <div className={styles.question_content_wrapper}>
            <p className="fs16 fw700">
              알레르기가 있는 것에{' '}
              <span className="text_underline">모두 체크</span>해 주세요.
            </p>
            <div className={styles.question_options_row}>
              <div className="w100">
                <AllergyCheckbox isRegister={isRegister && !isReadOnly} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MedicalHistoryChart;
